import { useState, useEffect, useMemo, useRef, type FocusEvent } from "react";
import { updateActiveTask, Util } from '../../util/Util'
import { useSale } from "./SaleContext";
import { useAuth } from "../../hooks/useAuth";
import { useUI } from "../../providers/MoleUIContext";
import RefreshButton from "../../components/RefreshButton";
import { Autocomplete, Box, Checkbox, Chip, Divider, FormControlLabel, IconButton, LinearProgress, Paper, Switch, TextField, Tooltip, Typography } from "@mui/material";
import { AllInclusive, ConfirmationNumber } from "@mui/icons-material";
import { SelectedPayments } from "../payments/PaymentModal";
import { isChangeTenderMode, isVoucherPayMode, isWalletPayMode, parseReceivedInput, paymentChangeAmount, receivedBelowAmount, seedPaymentReceived } from "../../util/constants";
import { MONEY_LIMITS, parseMoneyInput, roundMoney, addMoney } from "../../util/quantity";
import { useAppSocket } from "../../providers/AppSocketContext";
import {
	filterPayModesForWallet,
	isUsableWalletTender,
	useWalletTender,
	walletTenderCap,
} from "../wallet/useWalletTender";
import VoucherTenderModal from "../voucher/VoucherTenderModal";
import { voucherModeId, voucherTenderTotal, type VoucherTenderLine } from "../voucher/voucherTender";
import { canTenderFromWallet } from '../../util/access';
import { focusAndSelect } from "../../util/focusField";

interface Props {
	totalEnteredAmount: number;
}

interface PaymentMode {
	id: string;
	mode_name: string;
	is_active: boolean;
}

const PaymentDetail = ({ totalEnteredAmount }: Props) => {
	const [errorInput, setErrorInput] = useState("");
	const [cashReceived, setCashReceived] = useState<number>(0);
	const [isPaymentOptionsLoading, setIsPaymentOptionsLoading] = useState<boolean>(false);
	const [voucherModalOpen, setVoucherModalOpen] = useState(false);

	const { setPendingTasks, fetchedTask, activeTask, clientSituation, setClientSituation, agentSituation,
		setAgentSituation, isCreditLoading, setIsCreditLoading, isAgentCreditLoading, setIsAgentCreditLoading,
		useClientCredit, useAgentCredit, useBothCredit, findAccount, accountIsUnlimited,
		paymentModes, setPaymentModes,
	} = useSale();
	const payModes = paymentModes as PaymentMode[];
	const setPayModes = setPaymentModes;
	const { appSettings } = useAppSocket();
	const { api, access } = useAuth();
	const { t, queueReRun } = useUI();
	const canUseWalletInPos = canTenderFromWallet(access);
	const { wallet: clientWallet, loading: walletLoading } = useWalletTender({
		clientId: activeTask?.receiver,
		enabled: canUseWalletInPos && Number(activeTask?.receiver) > 0,
	});
	const showWallet = canUseWalletInPos && isUsableWalletTender(clientWallet);
	const voucherId = voucherModeId(payModes);
	const appliedVouchers = activeTask?.payment?.[voucherId]?.vouchers || [];
	const voucherTotal = voucherTenderTotal(appliedVouchers);
	const showVoucher = Boolean(activeTask?.payment?.[voucherId]?.enabled && voucherTotal > 0);
	const visibleModes = useMemo(() => {
		const modes = filterPayModesForWallet(payModes, showWallet);
		const voucherMode = payModes.find((mode) => isVoucherPayMode(mode.id));
		if (showVoucher && voucherMode && !modes.some((mode) => isVoucherPayMode(mode.id))) {
			return [...modes, voucherMode];
		}
		return modes;
	}, [payModes, showWallet, showVoucher]);
	const walletCap = walletTenderCap(clientWallet);

	const currentClientRef = useRef<number>(NaN);
	const currentAgentRef = useRef<number>(NaN);
	const initilizedPaymentsRef = useRef(false);
	const amountRefs = useRef<Record<string, HTMLInputElement | null>>({});
	const errorMessageRef = useRef("");

	const denominations = [10000, 5000, 2000, 1000, 500, 100, 50, 25];
	const balance = clientSituation?.after_total_balance ?? 0;
	const creditLimit = clientSituation?.credit_limit ?? 0;
	const isUnlimited = accountIsUnlimited(findAccount(activeTask?.receiver || -1));
	const percent = isUnlimited ? 0 : Math.min(100, Math.round((balance / (creditLimit || 1)) * 100));
	const barColor = percent < 70 ? "bg-success" : percent < 90 ? "bg-warning" : "bg-danger";
	// agent
	const agentBalance = agentSituation?.after_total_balance ?? 0;
	const agentCreditLimit = agentSituation?.credit_limit ?? 0;
	const isAgentUnlimited = accountIsUnlimited(findAccount(activeTask?.agent || -1));
	const agentPercent = isAgentUnlimited ? 0 : Math.min(100, Math.round((agentBalance / (agentCreditLimit || 1)) * 100));
	const agentBarColor = agentPercent < 70 ? "bg-success" : agentPercent < 90 ? "bg-warning" : "bg-danger";

	const selectedBalance = activeTask ? activeTask.total() : 0;
	const canPay = roundMoney(selectedBalance - totalEnteredAmount);

	const getSafePositiveAmount = (amount: number | undefined) =>
		Number.isFinite(amount) && (amount as number) > 0 ? (amount as number) : 0;

	const fetchPaymentOptions = async () => {
		setIsPaymentOptionsLoading(true);

		const res = await api.get("/payment/modes/active");

		if (res) {
			if (res.status === 200) {
				res.data.sort((a: any, b: any) => a.mode_name.localeCompare(b.mode_name));
				setPayModes(res.data);


				const newPaymentModes: SelectedPayments = {};

				res.data.forEach((mode: any) => {
					// If task already had this mode, keep it; otherwise init
					newPaymentModes[mode.id] = activeTask?.payment?.[mode.id] ?? {
						enabled: false,
						amount: 0,
					};
				});

				updateActiveTask(setPendingTasks, { payment: newPaymentModes });
			} else if (res.status === 401 || res.status === 422) {
				queueReRun(fetchPaymentOptions);
			} else {
				console.error("Failed to fetch data:", res);
			}
		}

		setIsPaymentOptionsLoading(false);
	};

	const fetchData = async (accountId: number, type: "client" | "agent" = "client") => {
		const account = findAccount(accountId);

		// short-circuiting to avoid fetching heavy data fetch when is not needed due to forwardness of walk-in client or unlimited account
		if (accountId === 1 || accountIsUnlimited(account)) {
			return;
		}

		if (activeTask?.receiver === activeTask?.agent || type === "client") {
			setIsCreditLoading(true);
		} else {
			setIsAgentCreditLoading(true);
		}

		const res = await api.get(`clients/${accountId}/situations`, {
			...(fetchedTask && {
				params: {
					excludeInvoice: fetchedTask.details.batch,
				}
			})
		});

		if (res) {
			if (res.status === 200) {
				if (activeTask?.receiver === activeTask?.agent) {
					setClientSituation(res.data);
					setAgentSituation(res.data);
				} else if (type === "client") {
					setClientSituation(res.data);
				} else if (type === "agent") {
					setAgentSituation(res.data);
				}
			} else if (res.status === 401 || res.status === 422) {
				queueReRun(fetchData, accountId, type);
			} else {
				console.error("Failed to fetch data:", res);
			}
		}

		if (activeTask?.receiver === activeTask?.agent || type === "client") {
			setIsCreditLoading(false);
		} else {
			setIsAgentCreditLoading(false);
		}
	};

	const issueChange = Boolean(activeTask?.issueChange);

	const applyVouchers = (lines: VoucherTenderLine[]) => {
		const total = voucherTenderTotal(lines);
		updateActiveTask(setPendingTasks, {
			payment: {
				...activeTask!.payment,
				[voucherId]: {
					enabled: total > 0,
					amount: total,
					vouchers: total > 0 ? lines : [],
				},
			},
		});
	};

	const setIssueChange = (next: boolean) => {
		const payment = { ...activeTask!.payment };
		Object.keys(payment).forEach((id) => {
			if (!payment[id]) return;
			if (!next) {
				payment[id] = { ...payment[id], received: 0 };
				return;
			}
			if (
				payment[id].enabled
				&& isChangeTenderMode(id)
				&& getSafePositiveAmount(payment[id].amount) > 0
			) {
				payment[id] = { ...payment[id], received: seedPaymentReceived(payment[id]) };
			}
		});
		updateActiveTask(setPendingTasks, { issueChange: next, payment });
	};

	const onToggleMode = (id: string) => {
		if (isVoucherPayMode(id)) return;
		const isEnabled = activeTask!.payment[id]?.enabled ?? false;
		// Keyboard: Space on the checkbox, then type the amount straight away.
		if (!isEnabled) focusAndSelect({ current: amountRefs.current[id] ?? null });
		const prefill = isWalletPayMode(id)
			? Math.max(0, Math.min(canPay, walletCap))
			: Math.max(0, canPay);
		updateActiveTask(setPendingTasks, {
			payment: {
				...activeTask!.payment,
				[id]: {
					...(activeTask!.payment[id] ?? { enabled: true, amount: 0 }),
					enabled: !isEnabled,
					amount: isEnabled ? 0 : prefill,
					received: isEnabled
						? 0
						: issueChange && isChangeTenderMode(id)
							? prefill
							: (activeTask!.payment[id]?.received || 0),
				},
			}
		});
	};

	const onAmountChange = (id: string, newAmount: number) => {
		if (isVoucherPayMode(id)) return;
		if (!Number.isFinite(newAmount)) {
			updateActiveTask(setPendingTasks, {
				payment: {
					...activeTask!.payment,
					[id]: {
						...activeTask!.payment[id],
						amount: 0
					}
				}
			});
			return;
		}

		newAmount = roundMoney(newAmount);
		if (newAmount < 0.01 || newAmount > MONEY_LIMITS.max) {
			setErrorInput(id);
			errorMessageRef.current = t("common:validation.amountRangeMin1", { max: Util.formatNumber(MONEY_LIMITS.max) });
			setTimeout(() => {
				setErrorInput("");
				errorMessageRef.current = ""
			}, 2000);
			return;
		}


		// Calculate potential new total
		const newTotal = roundMoney(Object.values(activeTask?.payment || {})
			.filter(payment => payment.enabled)
			.reduce((sum, payment) => addMoney(sum, getSafePositiveAmount(payment.amount)), 0)
			- getSafePositiveAmount(activeTask?.payment[id]?.amount)
			+ newAmount);

		if (isWalletPayMode(id) && newAmount > walletCap) {
			setErrorInput(id);
			errorMessageRef.current = t("common:wallet.tenderExceeds", { max: Util.formatNumber(walletCap) });
			setTimeout(() => {
				setErrorInput("");
				errorMessageRef.current = ""
			}, 3000);
			return;
		}

		if (newTotal > selectedBalance) {
			setErrorInput(id);
			errorMessageRef.current = t("common:validation.amountExceedsInvoice", { max: Util.formatNumber(selectedBalance) });
			setTimeout(() => {
				setErrorInput("");
				errorMessageRef.current = ""
			}, 3000);
			return;
		}

		const currentReceived = Math.max(0, Number(activeTask?.payment[id]?.received || 0));
		updateActiveTask(setPendingTasks, {
			payment: {
				...activeTask!.payment,
				[id]: {
					...activeTask!.payment[id],
					amount: newAmount,
					received: issueChange && isChangeTenderMode(id)
						? seedPaymentReceived({ amount: newAmount, received: currentReceived })
						: currentReceived > 0 ? Math.max(currentReceived, newAmount) : 0,
				}
			}
		});

	};

	const selectOnFocus = (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		event.target.select();
	};

	const focusReceived = (id: string) => (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		const tender = getSafePositiveAmount(activeTask?.payment[id]?.amount);
		const current = Math.max(0, Number(activeTask?.payment[id]?.received || 0));
		if (current <= 0 && tender > 0) {
			updateActiveTask(setPendingTasks, {
				payment: {
					...activeTask!.payment,
					[id]: {
						...activeTask!.payment[id],
						received: tender,
					},
				},
			});
		}
		requestAnimationFrame(() => event.target.select());
	};

	useEffect(() => {
		if ((Object.keys(activeTask?.payment || {}).length === 0) && !initilizedPaymentsRef.current) {
			const initialPayments: SelectedPayments = {};
			payModes.forEach((mode: any) => {
				initialPayments[mode.id] = { enabled: false, amount: 0 };
			});

			updateActiveTask(setPendingTasks, { payment: initialPayments });
			initilizedPaymentsRef.current = true;
		}
	}, [activeTask]);

	// Load payment modes once for the POS session; layout switches keep SaleContext cache.
	useEffect(() => {
		if (payModes.length === 0 && !isPaymentOptionsLoading) {
			void fetchPaymentOptions();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// TODO: review this is it is overkill
	const runSituationFetchEffect = (
		receiver: number,
		agent: number | null,
		clientChanged: boolean,
		agentChanged: boolean,
	) => {
		const clientSet = receiver >= 1;
		const sameAccount = clientSet && agent !== null && receiver === agent;

		if (sameAccount) {
			const sharedUnlimited = accountIsUnlimited(findAccount(receiver));
			const shouldFetch = useClientCredit ? clientChanged : (clientChanged || agentChanged);

			if (shouldFetch) {
				// AGENT policy: client-only move to a shared unlimited account → no fetch (TC-047)
				if (useAgentCredit && clientChanged && !agentChanged && sharedUnlimited) {
					return;
				}

				// agent-only on a shared unlimited account — agent path (TC-063, TC-095)
				if ((useAgentCredit || useBothCredit) && agentChanged && !clientChanged && sharedUnlimited) {
					fetchData(agent!, "agent");
					return;
				}

				fetchData(receiver);
			}
		} else {
			if (clientSet) {
				if (useClientCredit && clientChanged) {
					fetchData(receiver);
				} else if (useBothCredit && clientChanged) {
					if (agent === null || !accountIsUnlimited(findAccount(agent)) || !accountIsUnlimited(findAccount(receiver))) {
						fetchData(receiver);
					}
				} else if (useAgentCredit && agent === null && (clientChanged || agentChanged)) {
					fetchData(receiver);
				}
			}

			if (agent !== null && (useAgentCredit || useBothCredit) && agentChanged) {
				const agentUnlimited = accountIsUnlimited(findAccount(agent));
				// unlimited agent with no client set → no fetch (TC-061)
				if (!(useAgentCredit && !clientSet && agentUnlimited)) {
					fetchData(agent, "agent");
				}
			}
		}
	};

	const refreshSituations = () => {
		if (!activeTask || (!useClientCredit && !useAgentCredit && !useBothCredit)) {
			return;
		}

		const receiver = activeTask.receiver || -1;
		const agent = activeTask.agent ?? null;
		runSituationFetchEffect(receiver, agent, true, true);
	};

	useEffect(() => {
		const receiver = activeTask?.receiver || -1;
		const agent = activeTask?.agent ?? null;
		const agentRefVal = agent === null ? -1 : agent;

		if (receiver < 1) {
			setClientSituation(null);
		}
		if (agent === null) {
			setAgentSituation(null);
		}

		if (!activeTask || (!useClientCredit && !useAgentCredit && !useBothCredit)) {
			currentClientRef.current = receiver;
			currentAgentRef.current = agentRefVal;
			return;
		}

		// Layout switches remount PaymentDetail and reset refs to NaN. SaleContext keeps
		// client/agent situations — re-adopt selection without re-fetching when cache matches.
		if (Number.isNaN(currentClientRef.current) || Number.isNaN(currentAgentRef.current)) {
			currentClientRef.current = receiver;
			currentAgentRef.current = agentRefVal;
			const clientNeeds =
				receiver > 1 &&
				clientSituation?.id !== receiver &&
				!accountIsUnlimited(findAccount(receiver));
			const agentNeeds =
				agent !== null &&
				agentSituation?.id !== agent &&
				!accountIsUnlimited(findAccount(agent));
			if (clientNeeds || agentNeeds) {
				runSituationFetchEffect(receiver, agent, clientNeeds, agentNeeds);
			}
			return;
		}

		const clientChanged = currentClientRef.current !== receiver;
		const agentChanged = currentAgentRef.current !== agentRefVal;
		runSituationFetchEffect(receiver, agent, clientChanged, agentChanged);
		currentClientRef.current = receiver;
		currentAgentRef.current = agentRefVal;
	}, [activeTask?.receiver, activeTask?.agent]);

	useEffect(() => {
		if (walletLoading || showWallet || !activeTask?.payment) return;
		const walletEntry = Object.entries(activeTask.payment).find(([id]) => isWalletPayMode(id));
		if (!walletEntry || !walletEntry[1]?.enabled) return;
		updateActiveTask(setPendingTasks, {
			payment: {
				...activeTask.payment,
				[walletEntry[0]]: { ...walletEntry[1], enabled: false, amount: 0 },
			},
		});
	}, [showWallet, activeTask?.receiver]);

	return (
		<Box>
			< Paper elevation={24} sx={{ p: 2 }}>
				<Typography variant="h6" gutterBottom align="center">
					{t("common:ui.labels.creditInfo")}  <RefreshButton isWorking={isCreditLoading || isAgentCreditLoading} onClick={refreshSituations} />
				</Typography>

				{((
					(activeTask?.receiver ?? -1) > 1) &&
					(
						(useClientCredit && (isUnlimited || activeTask?.receiver === clientSituation?.id)) ||
						(useAgentCredit && (!activeTask?.agent || activeTask?.receiver === activeTask?.agent)) ||
						useBothCredit
					)
				) &&
					<>
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								gap: 2,
								mb: 1.5
							}}
						>
							<Divider sx={{ flexGrow: 1 }} />
							<Typography
								variant="subtitle1"
								fontWeight="bold"
								color="primary"
								sx={{ whiteSpace: 'nowrap', px: 2 }}
							>
								CLIENT
							</Typography>
							<Divider sx={{ flexGrow: 1 }} />
						</Box>
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
							<Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
								{t("common:ui.labels.status")}
							</Typography>

							{!isUnlimited ? (
								<Tooltip
									title={
										clientSituation?.is_agent ? (
											<div style={{ whiteSpace: 'pre-line' }}>
												Agent Unpaid: {Util.formatNumber(clientSituation?.after_agent_balance)}{'\n'}
												Client Unpaid: {Util.formatNumber(clientSituation?.after_client_balance)}{'\n'}
												Total Unpaid: {Util.formatNumber(balance)}{'\n'}
												Credit Limit: {Util.formatNumber(creditLimit)}
											</div>
										) : (
											<div style={{ whiteSpace: 'pre-line' }}>
												Total Unpaid: {Util.formatNumber(balance)}{'\n'}
												Credit Limit: {Util.formatNumber(creditLimit)}
											</div>
										)
									}
									arrow
								>
									<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1 }}>
										<LinearProgress
											variant="determinate"
											value={percent}
											color={
												barColor.includes('danger')
													? 'error'
													: barColor.includes('warning')
														? 'warning'
														: 'success'
											}
											sx={{
												height: 10,
												flexGrow: 1,
											}}
										/>
										<Typography variant="body2" sx={{ minWidth: 35, textAlign: 'right' }}>
											{`${percent}%`}
										</Typography>
									</Box>
								</Tooltip>
							) : (
								<Chip label={t("common:pos.unlimited")} color="primary" size="small" />
							)}
						</Box>
						<Typography variant="caption" color="text.primary" align="center">
							{t("common:ui.labels.creditLeft")}  {isUnlimited ? <AllInclusive /> :
								<Chip label={Util.formatCurrency(Math.max(0, creditLimit - balance), appSettings?.BIZ_CURRENCY?.value || "USD")} color={
									barColor.includes('danger')
										? 'error'
										: barColor.includes('warning')
											? 'warning'
											: 'success'
								} size="small" />}
						</Typography>
					</>
				}
				{((isAgentUnlimited && activeTask?.receiver !== activeTask?.agent) || ((activeTask?.agent ?? -1) > 1) && activeTask?.agent === agentSituation?.id && ((useAgentCredit || useBothCredit) && activeTask?.receiver !== activeTask?.agent)) &&
					<>
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								gap: 2,
								mb: 1.5
							}}
						>
							<Divider sx={{ flexGrow: 1 }} />
							<Typography
								variant="subtitle1"
								fontWeight="bold"
								color="secondary"
								sx={{ whiteSpace: 'nowrap', px: 2 }}
							>
								AGENT
							</Typography>
							<Divider sx={{ flexGrow: 1 }} />
						</Box>
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
							<Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
								{t("common:ui.labels.status")}
							</Typography>

							{!isAgentUnlimited ? (
								<Tooltip
									title={
										agentSituation?.is_agent ? (
											<div style={{ whiteSpace: 'pre-line' }}>
												Agent Unpaid: {Util.formatNumber(agentSituation?.after_agent_balance)}{'\n'}
												Client Unpaid: {Util.formatNumber(agentSituation?.after_client_balance)}{'\n'}
												Total Unpaid: {Util.formatNumber(agentBalance)}{'\n'}
												Credit Limit: {Util.formatNumber(agentCreditLimit)}
											</div>
										) : (
											<div style={{ whiteSpace: 'pre-line' }}>
												Total Unpaid: {Util.formatNumber(agentBalance)}{'\n'}
												Credit Limit: {Util.formatNumber(agentCreditLimit)}
											</div>
										)
									}
									arrow
								>
									<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1 }}>
										<LinearProgress
											variant="determinate"
											value={agentPercent}
											color={
												agentBarColor.includes('danger')
													? 'error'
													: agentBarColor.includes('warning')
														? 'warning'
														: 'success'
											}
											sx={{
												height: 10,
												flexGrow: 1,
											}}
										/>
										<Typography variant="body2" sx={{ minWidth: 35, textAlign: 'right' }}>
											{`${agentPercent}%`}
										</Typography>
									</Box>
								</Tooltip>
							) : (
								<Chip label={t("common:pos.unlimited")} color="primary" size="small" />
							)}
						</Box>
						<Typography variant="caption" color="text.primary" align="center">
							{t("common:ui.labels.creditLeft")}  {isAgentUnlimited ? <AllInclusive /> :
								<Chip label={Util.formatCurrency(Math.max(0, agentCreditLimit - agentBalance), appSettings?.BIZ_CURRENCY?.value || "USD")} color={
									agentBarColor.includes('danger')
										? 'error'
										: agentBarColor.includes('warning')
											? 'warning'
											: 'success'
								} size="small" />}
						</Typography>
					</>
				}
			</Paper>
			<Divider orientation="vertical" variant="middle" flexItem />
			< Paper elevation={24} sx={{ p: 2 }}>
				<Typography variant="h6" gutterBottom align="center">
					{t("common:ui.labels.paymentOptions")}
					<RefreshButton isWorking={isPaymentOptionsLoading} onClick={fetchPaymentOptions} />
					<Tooltip title={t("common:voucher.tender.open")}>
						<IconButton color="primary" onClick={() => setVoucherModalOpen(true)} size="small">
							<ConfirmationNumber />
						</IconButton>
					</Tooltip>
				</Typography>
				<FormControlLabel
					sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}
					control={
						<Switch
							size="small"
							checked={issueChange}
							onChange={(e) => setIssueChange(e.target.checked)}
						/>
					}
					label={t("common:voucher.issueChange")}
				/>

				{visibleModes.map((mode) => (
					<Box
						key={mode.id}
						display="flex"
						alignItems="center"
						justifyContent="space-between"
						sx={{ mb: 1 }}
					>
						<Checkbox
							checked={activeTask?.payment[mode.id]?.enabled || false}
							onChange={() => onToggleMode(mode.id)}
							disabled={isPaymentOptionsLoading || isVoucherPayMode(mode.id)}
						/>
						<Box sx={{ display: 'flex', gap: 1, flex: 1, minWidth: 0 }}>
							<TextField
								id={`amountInput-${mode.id}`}
								inputRef={(element: HTMLInputElement | null) => {
									amountRefs.current[mode.id] = element;
								}}
								size="small"
								label={mode.mode_name}
								variant="filled"
								fullWidth
								type="number"
								value={Number.isFinite(activeTask?.payment[mode.id]?.amount) ? activeTask?.payment[mode.id]?.amount : 0}
								required={activeTask?.payment[mode.id]?.enabled || false}
								disabled={!activeTask?.payment[mode.id]?.enabled || isPaymentOptionsLoading || isVoucherPayMode(mode.id)}
								onFocus={selectOnFocus}
								onChange={(e) => onAmountChange(mode.id, Number(e.target.value))}
								error={errorInput === mode.id}
								helperText={
									errorInput === mode.id
										? errorMessageRef.current
										: isWalletPayMode(mode.id) && clientWallet
											? t('common:wallet.tenderHint', { amount: Util.formatNumber(clientWallet.balance) })
											: undefined
								}
								sx={{ flex: 1, minWidth: 0 }}
								slotProps={{
									htmlInput: {
										min: 0,
										step: 0.01,
										...(isWalletPayMode(mode.id) ? { max: walletCap } : {}),
									},
								}}
							/>
							{issueChange
								&& isChangeTenderMode(mode.id)
								&& Boolean(activeTask?.payment[mode.id]?.enabled)
								&& Number(activeTask?.payment[mode.id]?.amount || 0) > 0 ? (
								<TextField
									size="small"
									label={t("common:voucher.receivedAmount")}
									variant="filled"
									type="number"
									value={Number.isFinite(activeTask?.payment[mode.id]?.received) ? activeTask?.payment[mode.id]?.received : 0}
									onFocus={focusReceived(mode.id)}
									onChange={(e) => {
										const next = parseReceivedInput(e.target.value);
										updateActiveTask(setPendingTasks, {
											payment: {
												...activeTask!.payment,
												[mode.id]: {
													...activeTask!.payment[mode.id],
													received: next,
												},
											},
										});
									}}
									error={receivedBelowAmount(activeTask?.payment[mode.id])}
									sx={{ flex: 1, minWidth: 0 }}
									helperText={
										receivedBelowAmount(activeTask?.payment[mode.id])
											? t('common:voucher.receivedBelowAmount', {
												amount: Util.formatNumber(activeTask?.payment[mode.id]?.amount || 0),
											})
											: t('common:voucher.changeHint', { amount: Util.formatNumber(paymentChangeAmount(activeTask?.payment[mode.id])) })
									}
									slotProps={{ htmlInput: { min: 0, step: 0.01 } }}
								/>
							) : null}
						</Box>
					</Box>
				))}

				<Typography variant="subtitle1" sx={{ mt: 2 }}>
					<strong>{t("common:ui.labels.totalToPay")}</strong>
					<Chip label={Util.formatCurrency(totalEnteredAmount, appSettings?.BIZ_CURRENCY?.value || "USD")} color={totalEnteredAmount > selectedBalance ? "error" : "success"} size="small" />
				</Typography>
			</Paper >
			<Divider orientation="vertical" variant="middle" flexItem />
			{!issueChange && activeTask?.payment[1]?.enabled &&
				<Paper elevation={24} sx={{ p: 2, mt: 2 }}>
					<Typography variant="h6" gutterBottom align="center">
						{t("common:ui.labels.changeCalculator")}
					</Typography>
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
						<Autocomplete
							size="small"
							freeSolo
							options={denominations}
							getOptionLabel={(option) => option.toString()}
							value={cashReceived}
							onInputChange={(_, newValue) => {
								const parsed = parseMoneyInput(newValue);
								setCashReceived(parsed === null ? 0 : parsed);
							}}
							renderInput={(params) => (
								<TextField
									{...params}
									label={t("common:ui.labels.amountReceived")}
									variant="filled"
									type="number"
									inputProps={{ min: 0, step: 0.01 }}
								/>
							)}
						/>

						<Typography variant="body1">
							<strong><em>{t("common:ui.labels.change")}&nbsp;</em></strong>
							<Chip label={Util.formatCurrency(activeTask
								? Math.max(
									0,
									cashReceived - (activeTask.payment[1]?.amount || 0)
								)
								: 0, appSettings?.BIZ_CURRENCY?.value || "USD")} color="warning" size="small" />
						</Typography>
					</Box>
				</Paper>
			}
			<VoucherTenderModal
				isOpen={voucherModalOpen}
				onClose={() => setVoucherModalOpen(false)}
				initialLines={appliedVouchers}
				maxSpend={Math.max(0, roundMoney(selectedBalance - totalEnteredAmount + voucherTotal))}
				onApply={applyVouchers}
			/>
		</Box>
	);
};

export default PaymentDetail;
